import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from "@mui/material";
import { IconButton, TextField } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import { formatCashFull } from "utils/formatCash";
import { FormikInvoice, InvoiceDetail } from "utils/invoices/invoiceFormat";
import { FormikProps } from "formik";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { v4 as uuidv4 } from "uuid";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Add01Icon,
  Delete02Icon,
  ImportIcon,
  Invoice01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import LineItemDialog, { type LineItemFields } from "./LineItemDialog";

type InvoiceItemsTablesProps = {
  formikInvoice: FormikProps<FormikInvoice>;
  handleOpenAttachedFilesDrawer: (params: { invoiceDetailId: string }) => void;
  showImportExpensesButton: boolean;
  onExpenseImport: () => void;
};

/**
 * Below this card width a line no longer fits on one row, so the lines become
 * a list edited in a dialog. Measured on the card, not the screen: beside the
 * summary rail the card is far narrower than the window.
 */
const WIDE_CARD = "@container (min-width: 940px)";
const INLINE_ONLY_SX = { display: "none", [WIDE_CARD]: { display: "block" } };
const LIST_ONLY_SX = { [WIDE_CARD]: { display: "none" } };

/** The figures keep fixed widths, so they line up down the rows whatever the text beside them. */
const FIGURE_WIDTH = { quantity: 120, price: 136, receipts: 72, total: 96 };

const LINE_GRID_SX = {
  display: "grid",
  alignItems: "center",
  columnGap: "var(--space-3)",
  gridTemplateColumns: "24px minmax(0, 2.5fr) minmax(0, 1.8fr) auto 40px",
  paddingBlock: "var(--space-3)",
};

const FIGURES_SX = {
  display: "flex",
  alignItems: "center",
  columnGap: "var(--space-3)",
};

/** A grid child that holds a control, as an ARIA cell so the row keeps its table semantics. */
const CELL_SX = { display: "flex", minWidth: 0 };

/** A price is typed, not stepped, so it hides the browser's arrows. */
const NO_SPIN_SX = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
};

/** The required marker, kept out of the translated label so a locale may reorder. */
const RequiredMark: React.FC = () => (
  <Typography
    component="span"
    variant="inherit"
    aria-hidden
    sx={{ color: "var(--color-text-danger)" }}
  >
    {" *"}
  </Typography>
);

const ColumnLabel: React.FC<{
  width?: number;
  align?: "right" | "center";
  children: React.ReactNode;
}> = ({ width, align, children }) => (
  <Typography
    role="columnheader"
    variant="subtitle2"
    sx={{ width, flexShrink: 0, textAlign: align }}
  >
    {children}
  </Typography>
);

interface EditorState {
  open: boolean;
  /** Where the line sits, or null for a new one. */
  index: number | null;
  /** Held from the moment it opened, so the dialog cannot change while it fades out. */
  item: InvoiceDetail | null;
}

const InvoiceItemsTable: React.FC<InvoiceItemsTablesProps> = (props) => {
  const { t } = useTranslation();
  const {
    formikInvoice,
    handleOpenAttachedFilesDrawer,
    showImportExpensesButton,
    onExpenseImport,
  } = props;

  const { handleFormikHelperText } = useFormikHelpers(formikInvoice);
  const [editor, setEditor] = useState<EditorState>({
    open: false,
    index: null,
    item: null,
  });

  const details = formikInvoice?.values?.details ?? [];
  const currency = formikInvoice.values.currency;

  const createBlankLineItem = (): InvoiceDetail => ({
    id: uuidv4(),
    invoiceId: formikInvoice.values.id || "",
    order: formikInvoice.values.details.length + 1,
    productOrService: "",
    description: "",
    amount: 0,
    total: 0,
    quantity: 1,
  });

  const handleDeleteLineItem = (detailId: string) => {
    const detailToDelete = formikInvoice.values.details.find(
      (item) => item.id === detailId,
    );

    if (detailToDelete) {
      formikInvoice.setFieldValue(
        "attachments",
        formikInvoice.values.attachments.filter(
          (attachment) => attachment.subParentId !== detailToDelete.id,
        ),
      );
    }

    const updatedDetails = formikInvoice.values.details
      .filter((item) => item.id !== detailId)
      .map((item, index) => ({
        ...item,
        order: index + 1, // Reassign order based on the new index
      }));

    formikInvoice.setFieldValue("details", updatedDetails);
  };

  const openEditor = (index: number | null) =>
    setEditor({
      open: true,
      index,
      item: index === null ? null : details[index],
    });
  const closeEditor = () =>
    setEditor((current) => ({ ...current, open: false }));

  const saveEditor = (fields: LineItemFields) => {
    if (editor.index === null) {
      formikInvoice.setFieldValue("details", [
        ...details,
        { ...createBlankLineItem(), ...fields },
      ]);
    } else {
      formikInvoice.setFieldValue(`details[${editor.index}]`, {
        ...details[editor.index],
        ...fields,
      });
    }
    closeEditor();
  };

  /** The first message a line's fields are showing, for a row that hides them. */
  const rowErrorFor = (index: number) =>
    (["productOrService", "quantity", "amount"] as const)
      .map((field) =>
        handleFormikHelperText(field, { parent: "details", index }),
      )
      .find(Boolean);

  const receiptsButton = (item: InvoiceDetail) => (
    <Badge
      badgeContent={
        formikInvoice.values.attachments.filter(
          (attachment) => attachment.subParentId === item.id,
        ).length
      }
      color="primary"
      overlap="circular"
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <IconButton
        aria-label={t("Receipts")}
        onClick={() => {
          handleOpenAttachedFilesDrawer({ invoiceDetailId: item.id });
        }}
      >
        <HugeiconsIcon icon={Invoice01Icon} size={16} />
      </IconButton>
    </Badge>
  );

  return (
    <Stack sx={{ padding: "var(--space-6)", containerType: "inline-size" }}>
      <Stack gap="var(--space-2)">
        <Typography variant="h5">{t("Line Items")}</Typography>
        {typeof formikInvoice.errors.details === "string" && (
          <Typography
            variant="caption"
            sx={{ color: "var(--color-text-danger)" }}
          >
            {handleFormikHelperText("details")}
          </Typography>
        )}
      </Stack>

      {details.length > 0 && (
        <Box role="table" aria-label={t("Line Items")} sx={INLINE_ONLY_SX}>
          <Box
            role="row"
            sx={{
              ...LINE_GRID_SX,
              borderBottom: "var(--border-width-1) solid var(--color-border)",
            }}
          >
            <ColumnLabel align="center">{t("#")}</ColumnLabel>
            <ColumnLabel>
              {t("Product or Service")}
              <RequiredMark />
            </ColumnLabel>
            <ColumnLabel>{t("Description")}</ColumnLabel>
            <Box sx={FIGURES_SX}>
              <ColumnLabel width={FIGURE_WIDTH.quantity}>
                {t("Quantity")}
                <RequiredMark />
              </ColumnLabel>
              <ColumnLabel width={FIGURE_WIDTH.price}>
                {t("Unit Price")}
                <RequiredMark />
              </ColumnLabel>
              <ColumnLabel width={FIGURE_WIDTH.receipts} align="center">
                {t("Receipts")}
              </ColumnLabel>
              <ColumnLabel width={FIGURE_WIDTH.total} align="right">
                {t("Total")}
              </ColumnLabel>
            </Box>
            <Box role="columnheader" aria-label={t("Remove line item")} />
          </Box>

          {details.map((item, index) => (
            <Box key={item.id} role="row" sx={LINE_GRID_SX}>
              <Typography
                role="cell"
                variant="body2"
                sx={{ textAlign: "center" }}
              >
                {index + 1}
              </Typography>

              <Box role="cell" sx={CELL_SX}>
                <TextField
                  id={`line-item-${item.id}-product`}
                  name={`details[${index}].productOrService`}
                  fullWidth
                  size="md"
                  inputProps={{ "aria-label": t("Product or Service") }}
                  value={item.productOrService}
                  onChange={formikInvoice.handleChange}
                  onBlur={formikInvoice.handleBlur}
                  errorText={
                    handleFormikHelperText("productOrService", {
                      parent: "details",
                      index,
                    }) || undefined
                  }
                />
              </Box>

              <Box role="cell" sx={CELL_SX}>
                <TextField
                  id={`line-item-${item.id}-description`}
                  name={`details[${index}].description`}
                  fullWidth
                  size="md"
                  inputProps={{ "aria-label": t("Description") }}
                  value={item.description}
                  onChange={formikInvoice.handleChange}
                />
              </Box>

              <Box sx={FIGURES_SX}>
                <Box
                  role="cell"
                  sx={{ width: FIGURE_WIDTH.quantity, flexShrink: 0 }}
                >
                  <TextField
                    id={`line-item-${item.id}-quantity`}
                    name={`details[${index}].quantity`}
                    type="number"
                    fullWidth
                    size="md"
                    value={item.quantity}
                    onChange={formikInvoice.handleChange}
                    onBlur={formikInvoice.handleBlur}
                    errorText={
                      handleFormikHelperText("quantity", {
                        parent: "details",
                        index,
                      }) || undefined
                    }
                    inputProps={{
                      "aria-label": t("Quantity"),
                      min: 0,
                      onWheel: (event) => event.currentTarget.blur(),
                    }}
                  />
                </Box>

                <Box
                  role="cell"
                  sx={{ width: FIGURE_WIDTH.price, flexShrink: 0 }}
                >
                  <TextField
                    id={`line-item-${item.id}-amount`}
                    name={`details[${index}].amount`}
                    type="number"
                    fullWidth
                    size="md"
                    value={item.amount}
                    onChange={formikInvoice.handleChange}
                    onBlur={formikInvoice.handleBlur}
                    errorText={
                      handleFormikHelperText("amount", {
                        parent: "details",
                        index,
                      }) || undefined
                    }
                    inputProps={{
                      "aria-label": t("Unit Price"),
                      style: { textAlign: "right" },
                      onWheel: (event) => event.currentTarget.blur(),
                    }}
                    sx={NO_SPIN_SX}
                  />
                </Box>

                <Box
                  role="cell"
                  sx={{
                    width: FIGURE_WIDTH.receipts,
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {receiptsButton(item)}
                </Box>

                <Typography
                  role="cell"
                  variant="body1"
                  sx={{
                    width: FIGURE_WIDTH.total,
                    flexShrink: 0,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatCashFull(item.total, currency)}
                </Typography>
              </Box>

              <Box role="cell" sx={CELL_SX}>
                <IconButton
                  variant="ghost"
                  aria-label={t("Remove line item")}
                  onClick={() => handleDeleteLineItem(item.id)}
                >
                  <HugeiconsIcon icon={Delete02Icon} size={18} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {details.length > 0 && (
        <List disablePadding sx={LIST_ONLY_SX}>
          {details.map((item, index) => {
            const rowError = rowErrorFor(index);
            return (
              <ListItem
                key={item.id}
                disablePadding
                divider
                sx={{ gap: "var(--space-2)" }}
              >
                <ListItemButton
                  onClick={() => openEditor(index)}
                  sx={{ minWidth: 0 }}
                >
                  <Stack gap="var(--space-1)" sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                      {`${index + 1}. `}
                      {item.productOrService || (
                        <Typography
                          component="span"
                          variant="inherit"
                          sx={{ color: "var(--color-text-tertiary)" }}
                        >
                          {t("Product or Service")}
                        </Typography>
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        color: rowError
                          ? "var(--color-text-danger)"
                          : "var(--color-text-tertiary)",
                      }}
                    >
                      {rowError ??
                        [
                          `${item.quantity} × ${formatCashFull(item.amount, currency)}`,
                          item.description,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="subtitle2"
                    sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatCashFull(item.total, currency)}
                  </Typography>
                </ListItemButton>
                {/* Beside the row rather than over it, so it can never cover the amount. */}
                <Box sx={{ flexShrink: 0 }}>{receiptsButton(item)}</Box>
              </ListItem>
            );
          })}
        </List>
      )}

      <Inline>
        <Button
          size="small"
          variant="primary-soft"
          onClick={() =>
            formikInvoice.setFieldValue("details", [
              ...details,
              createBlankLineItem(),
            ])
          }
          sx={{
            alignSelf: "flex-start",
            display: "none",
            [WIDE_CARD]: { display: "inline-flex" },
          }}
        >
          <HugeiconsIcon icon={Add01Icon} size={16} />
          {t("Add Line Item")}
        </Button>
        <Button
          size="small"
          variant="primary-soft"
          onClick={() => openEditor(null)}
          sx={{ alignSelf: "flex-start", ...LIST_ONLY_SX }}
        >
          <HugeiconsIcon icon={Add01Icon} size={16} />
          {t("Add Line Item")}
        </Button>
        {showImportExpensesButton && (
          <Button
            size="small"
            variant="default-soft"
            onClick={onExpenseImport}
            sx={{ alignSelf: "flex-start" }}
          >
            <HugeiconsIcon icon={ImportIcon} size={16} />
            {t("Import Expenses")}
          </Button>
        )}
      </Inline>

      <LineItemDialog
        open={editor.open}
        item={editor.item}
        currency={currency}
        onClose={closeEditor}
        onSave={saveEditor}
        onRemove={
          editor.item
            ? () => {
                handleDeleteLineItem(editor.item?.id ?? "");
                closeEditor();
              }
            : undefined
        }
      />
    </Stack>
  );
};

export default InvoiceItemsTable;
